import React from 'react';
import { Box, Container, Typography, Grid, Divider, Link as MuiLink } from '@mui/material';
import { Mail, MapPin, Phone, ExternalLink } from 'lucide-react';
import { GitHubBrandIcon } from './BrandIcons';
import { APP_VERSION } from '../version';

export const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0F172A',
        color: '#F8FAFC',
        pt: 6,
        pb: 4,
        mt: 'auto',
        borderTop: '3px solid #2563EB',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box
                component="span"
                sx={{
                  bgcolor: '#2563EB',
                  color: '#FFFFFF',
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 1.5,
                  fontFamily: '"Prompt", sans-serif',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                }}
              >
                TPMS
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '1.1rem' }}>
                Teacher Practicum Management System (TPMS)
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#CBD5E1', lineHeight: 1.8, maxWidth: 620, fontSize: '0.9rem' }}>
              ระบบบริหารจัดการงานฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร (KPRU)
              บูรณาการการจัดสรรสถานศึกษา การนิเทศการสอน การประเมินผลสมรรถนะ ภาระงาน และคลังงานวิจัยในชั้นเรียนครบวงจร
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#60A5FA', mb: 1.5, fontSize: '0.95rem' }}>
              ติดต่อฝ่ายฝึกประสบการณ์วิชาชีพครู
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: '#CBD5E1', fontSize: '0.9rem' }}>
                <MapPin size={16} style={{ marginTop: 4, flexShrink: 0, color: '#60A5FA' }} />
                <Typography variant="body2" sx={{ color: '#CBD5E1', lineHeight: 1.7, fontSize: '0.88rem' }}>
                  ฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร<br />
                  เลขที่ 69 หมู่ 1 ต.นครชุม อ.เมือง จ.กำแพงเพชร 62000
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#CBD5E1' }}>
                <Phone size={15} style={{ flexShrink: 0, color: '#60A5FA' }} />
                <Typography variant="body2" sx={{ color: '#CBD5E1', fontSize: '0.88rem' }}>
                  โทรศัพท์: <strong>0-5570-6555 ต่อ 2010</strong>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3.5, borderColor: 'rgba(255, 255, 255, 0.14)' }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <MuiLink
            href="https://edu.kpru.ac.th"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#94A3B8',
              fontSize: '0.85rem',
              textAlign: { xs: 'center', sm: 'left' },
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              transition: 'color 0.2s',
              '&:hover': { color: '#60A5FA', textDecoration: 'underline' },
            }}
          >
            <span>คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร (KPRU)</span>
            <ExternalLink size={13} />
          </MuiLink>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <MuiLink
              href="https://github.com/tswiwat/tpms"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                color: '#CBD5E1',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                px: 1.5,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: '0.82rem',
                fontFamily: '"Prompt", sans-serif',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.18)',
                  borderColor: '#60A5FA',
                  color: '#FFFFFF',
                },
              }}
            >
              <GitHubBrandIcon size={15} />
              <span>เวอร์ชัน {APP_VERSION}</span>
            </MuiLink>

            <MuiLink
              href="mailto:wiwat_t@kpru.ac.th"
              sx={{
                bgcolor: 'rgba(37, 99, 235, 0.2)',
                color: '#93C5FD',
                border: '1px solid rgba(147, 197, 253, 0.3)',
                px: 1.5,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: '0.84rem',
                fontFamily: '"Prompt", sans-serif',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(37, 99, 235, 0.35)',
                  borderColor: '#93C5FD',
                  color: '#FFFFFF',
                },
              }}
            >
              <Mail size={15} />
              <span>ผู้พัฒนาระบบ วิวัฒน์ ทวีทรัพย์</span>
            </MuiLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
